import { type ReactNode, useEffect, useRef } from 'react';

import { KeyboardEngine } from './keyboard-engine';
import { KeyboardEngineContext } from './KeyboardEngineContext';

// ─── Provider ─────────────────────────────────────────────────────────────────

interface KeyboardEngineProviderProps {
  children: ReactNode;
  /**
   * DOM element to attach the `keydown` listener to.
   * Default: `document`.
   */
  target?: Document | HTMLElement;
  /**
   * Whether to use the capture phase for the `keydown` listener.
   *
   * **Must remain `true`** (the default) when used inside `ERPCoreProvider`.
   * The engine is placed as the outermost provider so its capture-phase
   * listener registers BEFORE the ShortcutManager, ModalManager, and
   * FocusManager listeners. This ordering guarantees that:
   *
   *  - `engine.pause()` calls `stopImmediatePropagation`, blocking every
   *    subsequent manager from receiving the event.
   *  - Engine handlers at high priority (e.g. `KEYBOARD_PRIORITY.FIELD_NAVIGATION`)
   *    can consume events before ShortcutManager sees them.
   *
   * Set to `false` only in isolated test environments.
   */
  capture?: boolean;
  /**
   * Enable debug mode on mount.
   * All key events are logged and stored in history.
   * Safe to enable in development; disable for production.
   * Default: `false`.
   */
  debug?: boolean;
}

/**
 * Provides a `KeyboardEngine` to the React subtree.
 *
 * **MUST be the outermost provider in `ERPCoreProvider`** so that its
 * capture-phase `keydown` listener registers before all other managers:
 *
 * ```
 * ERPCoreContext
 *   KeyboardEngineProvider   ← registers first → pause() blocks everything
 *     ShortcutManagerProvider
 *       FocusManagerProvider
 *         ModalManagerProvider
 *           DraftManagerProvider
 *             {children}
 * ```
 *
 * ## Responsibilities
 * - Creates a `KeyboardEngine` instance (stable across re-renders).
 * - Attaches a single `keydown` listener (capture phase) that calls
 *   `engine.dispatch(event)` for every trusted key event.
 * - Optionally enables debug mode on mount.
 *
 * @example
 * // Default (inside ERPCoreProvider):
 * <KeyboardEngineProvider debug={import.meta.env.DEV}>
 *   <ShortcutManagerProvider>
 *     ...
 *   </ShortcutManagerProvider>
 * </KeyboardEngineProvider>
 */
export function KeyboardEngineProvider({
  children,
  target,
  capture = true,
  debug = false,
}: KeyboardEngineProviderProps) {
  // Create engine once — stable across re-renders
  const engineRef = useRef<KeyboardEngine | null>(null);
  engineRef.current ??= new KeyboardEngine();
  const engine = engineRef.current;

  // Apply debug mode prop
  useEffect(() => {
    engine.setDebugMode(debug);
  }, [engine, debug]);

  // Attach the single keydown listener
  useEffect(() => {
    const el: Document | HTMLElement = target ?? document;

    function handleKeyDown(event: Event): void {
      engine.dispatch(event as KeyboardEvent);
    }

    el.addEventListener('keydown', handleKeyDown, { capture });

    return () => {
      el.removeEventListener('keydown', handleKeyDown, { capture });
    };
  }, [engine, target, capture]);

  return (
    <KeyboardEngineContext.Provider value={{ engine }}>{children}</KeyboardEngineContext.Provider>
  );
}
